import React, { useState, useEffect } from "react";
import { ReadKPI, ListKPIs, ListAppIds } from "./api";
import { bucketFromNow } from "./hooks/time-buckets";
import Input from "aldoo-ra/Input";
import DropDownSelect from "aldoo-ra/DropdownSelect";
import Button from "aldoo-ra/Button";
import Typography from "aldoo-ra/Typography";
import LineChartWidget from "./components/charts/LineChartWidget";
import BarChartWidget from "./components/charts/BarChartWidget";
import TableWidget from "./components/charts/TableWidget";
import PieChartWidget from "./components/charts/PieChartWidget";

const TIME_RANGES = ["30m", "1h", "6h", "24h", "7d", "30d", "90d"].map((v) => ({ value: v, label: v }));
const GRANULARITIES = ["second", "minute", "hour", "day"].map((v) => ({ value: v, label: v }));
const AGGREGATIONS = ["latest", "avg", "max", "min", "sum", "count"].map((v) => ({ value: v, label: v }));
const VIZ_OPTIONS = [
    { value: "line", label: "Line" },
    { value: "bar", label: "Bar" },
    { value: "table", label: "Table" },
    { value: "pie", label: "Pie" },
];
const VIZ_TYPES = { line: LineChartWidget, bar: BarChartWidget, table: TableWidget, pie: PieChartWidget };

const selVal = (sel) => (Array.isArray(sel) ? sel[0]?.value : sel?.value);

export default function KPIExplorer() {
    const [kpiOptions, setKpiOptions] = useState([]);
    const [appIdOptions, setAppIdOptions] = useState([]);
    const [kpi, setKpi] = useState("");
    const [appId, setAppId] = useState("");
    const [timeRange, setTimeRange] = useState("24h");
    const [granularity, setGranularity] = useState("hour");
    const [aggregation, setAggregation] = useState("avg");
    const [visualization, setVisualization] = useState("line");
    const [groupByTag, setGroupByTag] = useState("");
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingMetadata, setLoadingMetadata] = useState(true);
    const [queryError, setQueryError] = useState("");

    useEffect(() => {
        Promise.all([ListKPIs(), ListAppIds()]).then(([kpiRes, appRes]) => {
            if (kpiRes.success) {
                const opts = (kpiRes.kpis || []).map((k) => ({ value: k.id, label: `${k.name} (${k.id})` }));
                setKpiOptions(opts);
            }
            if (appRes.success) {
                const ids = appRes.appIds || [];
                const opts = ids.map((id) => ({ value: id, label: id }));
                setAppIdOptions(opts);
                if (ids.length === 1) setAppId(ids[0]);
            }
            setLoadingMetadata(false);
        });
    }, []);

    const handleQuery = () => {
        setQueryError("");
        if (!kpi) { setQueryError("Please select a KPI"); return; }
        if (!appId) { setQueryError("Please enter an App ID"); return; }

        setLoading(true);
        setResult(null);

        const { from, to } = bucketFromNow(timeRange);
        ReadKPI({
            kpi,
            appId,
            from: from.toISOString(),
            to: to.toISOString(),
            granularity,
            aggregation,
            groupByTag: groupByTag || null,
        })
            .then((res) => setResult(res))
            .catch((e) => setResult({ success: false, error: e.message }))
            .finally(() => setLoading(false));
    };

    const ChartViz = VIZ_TYPES[visualization] || VIZ_TYPES.line;

    return (
        <div style={{ padding: "1.5rem" }}>
            <Typography variant="admin_h2" style={{ margin: "0 0 0.25rem" }}>KPI Explorer</Typography>
            <Typography variant="admin_label" style={{ marginBottom: "1rem" }}>
                Select a KPI and App ID, then click Query to fetch time-series data
            </Typography>

            <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "0.75rem", alignItems: "flex-end" }}>
                <DropDownSelect
                    variant="admin"
                    options={kpiOptions}
                    value={kpi ? [{ value: kpi, label: kpiOptions.find((o) => o.value === kpi)?.label || kpi }] : []}
                    onChange={(sel) => { setKpi(selVal(sel) || ""); setQueryError(""); }}
                    placeholder="Select KPI"
                    loading={loadingMetadata}
                    searchEnabled
                />
                <DropDownSelect
                    variant="admin"
                    options={appIdOptions}
                    value={appId ? [{ value: appId, label: appId }] : []}
                    onChange={(sel) => { setAppId(selVal(sel) || ""); setQueryError(""); }}
                    placeholder="Select App"
                    loading={loadingMetadata}
                    searchEnabled
                    editable
                    creatable
                />
                <DropDownSelect
                    variant="admin"
                    options={TIME_RANGES}
                    value={[{ value: timeRange, label: timeRange }]}
                    onChange={(sel) => setTimeRange(selVal(sel) || "24h")}
                />
                <DropDownSelect
                    variant="admin"
                    options={GRANULARITIES}
                    value={[{ value: granularity, label: granularity }]}
                    onChange={(sel) => setGranularity(selVal(sel) || "hour")}
                />
                <DropDownSelect
                    variant="admin"
                    options={AGGREGATIONS}
                    value={[{ value: aggregation, label: aggregation }]}
                    onChange={(sel) => setAggregation(selVal(sel) || "avg")}
                />
                <DropDownSelect
                    variant="admin"
                    options={VIZ_OPTIONS}
                    value={[{ value: visualization, label: VIZ_OPTIONS.find((o) => o.value === visualization)?.label || visualization }]}
                    onChange={(sel) => setVisualization(selVal(sel) || "line")}
                />
                <Input
                    variant="admin"
                    label="Group By Tag"
                    value={groupByTag}
                    onChange={(e) => setGroupByTag(e.target.value)}
                    placeholder="e.g. provider"
                />
                <Button variant="admin_primary" onClick={handleQuery} loading={loading}>
                    Query
                </Button>
            </div>

            {queryError && (
                <Typography variant="admin_label" style={{ color: "#f59e0b", marginBottom: "0.5rem" }}>
                    {queryError}
                </Typography>
            )}

            {result && (
                <div style={{ marginTop: "1rem", background: "var(--admin_paper, #fff)", borderRadius: 8, padding: "1rem", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
                    {result.success ? (
                        <div>
                            <div style={{ marginBottom: "0.5rem", fontSize: "0.85rem", color: "var(--admin_neutral_3, #64748b)" }}>
                                {result.buckets?.length || 0} data points | {result.granularity} buckets | {result.aggregation}
                            </div>
                            <ChartViz data={result.buckets || []} granularity={granularity} tagKey={groupByTag || undefined} />
                        </div>
                    ) : (
                        <div style={{ color: "#ef4444" }}>Error: {result.error}</div>
                    )}
                </div>
            )}
        </div>
    );
}
